/**
 * The accountant's .xlsx — one workbook, one sheet per table.
 *
 * Built with `write-excel-file` (browser build, no server round trip), and
 * loaded on demand so the spreadsheet code is not in the page's first load.
 * Figures stay as numbers where they are numbers, so Excel can add them up;
 * everything else is text.
 */
export interface XlsxSheet {
  /** Excel caps a sheet name at 31 characters and forbids : \ / ? * [ ]. */
  name: string;
  headers: string[];
  rows: string[][];
}

const NUMBER = /^-?\d+(\.\d+)?$/;

export function sheetName(raw: string, used: Set<string> = new Set()): string {
  const base =
    raw
      .replace(/[:\\/?*[\]]/g, " ")
      .trim()
      .slice(0, 31) || "Sheet";
  let name = base;
  for (let n = 2; used.has(name.toLowerCase()); n += 1) {
    name = `${base.slice(0, 31 - String(n).length - 1)} ${n}`;
  }
  used.add(name.toLowerCase());
  return name;
}

export function toSheetData(headers: string[], rows: string[][]) {
  return [
    headers.map((h) => ({ value: h, fontWeight: "bold" as const })),
    ...rows.map((r) =>
      r.map((c) =>
        NUMBER.test(c)
          ? { value: Number(c), type: Number }
          : { value: c, type: String },
      ),
    ),
  ];
}

export async function exportXlsx(
  filename: string,
  sheets: XlsxSheet[],
): Promise<void> {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const used = new Set<string>();
  const book = sheets.map((s) => ({
    sheet: sheetName(s.name, used),
    data: toSheetData(s.headers, s.rows),
  }));
  await writeXlsxFile(book as never).toFile(
    filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`,
  );
}
